import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db, COLLECTIONS } from './config';

// Get active live classes for a student
export const getActiveLiveClasses = async (studentId) => {
  try {
    // Query active Daily.co rooms — single where, sort client-side to avoid index
    const q = query(
      collection(db, COLLECTIONS.DAILYCO_ROOMS),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);

    const classes = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      startTime: d.data().createdAt?.toDate?.() || d.data().startTime?.toDate?.() || null,
      createdAt: d.data().createdAt?.toDate?.() || null,
      updatedAt: d.data().updatedAt?.toDate?.() || null
    }));

    // Sort by most recent first
    classes.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    // Also fetch live classes from bookings that have zoom links (confirmed lessons happening now)
    const today = new Date().toISOString().split('T')[0];
    let bookingClasses = [];
    try {
      const bookingsQ = query(
        collection(db, COLLECTIONS.BOOKINGS),
        where('status', '==', 'confirmed')
      );
      const bookingsSnap = await getDocs(bookingsQ);
      bookingClasses = bookingsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(b => b.zoomLink && b.date === today)
        .map(b => ({
          id: b.id,
          title: `Lesson with ${b.teacherName}`,
          teacherName: b.teacherName,
          teacherId: b.teacherId,
          subject: b.language || 'Language',
          description: `${b.startTime} - ${b.endTime}`,
          url: b.zoomLink,
          status: 'active',
          startTime: new Date(`${b.date}T${b.startTime}`),
          createdAt: new Date(`${b.date}T${b.startTime}`),
          isBooking: true
        }));
    } catch (e) {
      console.warn('Could not fetch booking classes:', e);
    }

    return [...classes, ...bookingClasses];
  } catch (error) {
    console.error('Error fetching live classes:', error);
    throw error;
  }
};

// Get active Daily.co rooms
export const getActiveDailyRooms = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.DAILYCO_ROOMS),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.(),
        updatedAt: d.data().updatedAt?.toDate?.()
      }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } catch (error) {
    console.error('Error fetching Daily.co rooms:', error);
    throw error;
  }
};

// Get a specific Daily.co room by ID
export const getDailyRoom = async (roomId) => {
  try {
    const roomRef = doc(db, COLLECTIONS.DAILYCO_ROOMS, roomId);
    const roomSnap = await getDoc(roomRef);
    
    if (!roomSnap.exists()) {
      throw new Error('Room not found');
    }

    const roomData = roomSnap.data();
    return {
      id: roomSnap.id,
      ...roomData,
      createdAt: roomData.createdAt?.toDate(),
      updatedAt: roomData.updatedAt?.toDate()
    };
  } catch (error) {
    console.error('Error fetching Daily.co room:', error);
    throw error;
  }
};
